import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { OSName, getOSName } from '@sas/nova-commons/util';
export function useDebounce(initialValue, timeout) {
    const [value, setActualValue] = useState(initialValue);
    const timeoutId = useRef(0);
    const setValue = useCallback((value, force = false) => {
        if (force) {
            setActualValue(value);
            return;
        }
        const hasTimeout = !!timeoutId.current;
        if (timeoutId.current) {
            clearInterval(timeoutId.current);
        }
        timeoutId.current = window.setTimeout(() => {
            timeoutId.current = 0;
        }, timeout);
        if (hasTimeout) {
            return;
        }
        setActualValue(value);
    }, 
    // if timeout changes, we don't want to create a new callback. (only initial value is used)
    [] // eslint-disable-line react-hooks/exhaustive-deps
    );
    return [value, setValue];
}
export function useContextMenuHandlers(onReportContextMenu) {
    const refOnReportContextMenu = useRef(onReportContextMenu);
    useEffect(() => {
        refOnReportContextMenu.current = onReportContextMenu;
    }, [onReportContextMenu]);
    return useMemo(() => {
        const onKeyDown = (e) => {
            if (refOnReportContextMenu.current &&
                (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) &&
                // Shift+F10 generates a contextmenu event on Windows so avoid creating our own
                getOSName() !== OSName.Win) {
                e.preventDefault();
                const targetElement = e.target instanceof HTMLElement ? e.target : document.activeElement;
                const { x = 0, y = 0 } = (targetElement === null || targetElement === void 0 ? void 0 : targetElement.getBoundingClientRect()) || {};
                refOnReportContextMenu.current({
                    x,
                    y,
                    source: 'keyboard',
                    nativeEvent: e.nativeEvent,
                });
            }
        };
        const onContextMenu = (e) => {
            if (refOnReportContextMenu.current) {
                e.preventDefault();
                refOnReportContextMenu.current({
                    x: e.pageX,
                    y: e.pageY,
                    source: 'mouse',
                    nativeEvent: e.nativeEvent,
                });
            }
        };
        return { onKeyDown, onContextMenu };
    }, []);
}
export function useLatestRef(value) {
    const ref = useRef(value);
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref;
}
export function useTimeout(callback, timeout) {
    const refCallback = useLatestRef(callback);
    useEffect(() => {
        const timeoutId = setTimeout(() => refCallback.current(), timeout);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [timeout, refCallback]);
}
export function useOnUnmount(onUnmount) {
    const refOnUnmount = useLatestRef(onUnmount);
    useEffect(() => () => refOnUnmount.current(), [refOnUnmount]);
}
/**
 * Hook for running render logic reactively. This has better performance than useEffect + setState, but it should be
 * used with caution and avoided when possible.
 *
 * Further reading: https://react.dev/reference/react/useState#storing-information-from-previous-renders
 *
 * There is no cleanup phase because the callback should contain no effects external to the component.
 *
 * @param callback callback to run reactively as dependencies change. Must be safe to call synchronously during render.
 * @param deps callback dependencies
 * @returns Whether dependencies changed. When true, a rerender has been scheduled and the caller may choose to bail out of the current render.
 */
export function useRenderEffect(callback, deps) {
    const [prevDeps, setPrevDeps] = useState();
    const changed = prevDeps === undefined || deps.some((d, i) => !Object.is(d, prevDeps[i]));
    if (changed) {
        setPrevDeps(deps);
        callback();
    }
    return changed;
}
